import {
    Box,
    Button,
    Form,
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { DesktopProcessActions, DesktopProcessMessageType, HardwareProcessStatus } from '@common/enums';
import { ManagerAuthorization } from '@common/enums';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { PageTemplate } from '@frontend/components/templates/PageTemplate';
import { fetchJson } from '@frontend/store/fetch';
import { translateKey } from '@frontend/translate';
import { assertHasAuthorization } from '@frontend/utils/assertHasAuthorization';
import { useSearch } from '@frontend/utils/hooks/useSearch';
import { FullName, HeaderGroup, Loader } from '../../elements/common';
import { ListPager } from '../../elements/ListPager';
import { NullableFormattedDatetime } from '../../elements/NullableFormattedDatetime';
import { OrganizationSelection } from '../../elements/OrganizationSelection';
import { DesktopProcessAccessReasonsModal } from './DesktopProcessAccessReasonsModal';

interface IDesktopProcess {
    action: DesktopProcessActions;
    created_at: string;
    employment: {
        practitioner: {
            nameFamily: string;
            nameGiven: string | null;
            namePrefix: string | null;
        };
    };
    id: string;
    messages?: {
        content: any;
        created_at: string;
        desktopProcess: string;
        id: string;
        type: DesktopProcessMessageType;
        updated_at: string;
    }[];
    params?: any;
    request?: {
        patient: {
            nameFamily: string;
            nameFamily2: string | null;
            nameGiven: string | null;
            nameInfix: string | null;
            nameInfix2: string | null;
            nameInitials: string | null;
            namePrefix: string | null;
            nameSuffix: string | null;
            nameUsual: string | null;
        };
        processId: number;
    };
    status: HardwareProcessStatus;
    updated_at: string;
}

export interface ISearchState {
    itemsPerPage: number;
    organizationId?: string;
    page: number;
}

export function DesktopProcessesList() {
    const [desktopProcesses, setDesktopProcesses] = useState<IDesktopProcess[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [searchState, setSearchState] = useSearch<ISearchState>({ page: 1, itemsPerPage: 20 });
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [hasReasonForAccess, setHasReasonForAccess] = useState(false);

    useEffect(() => {
        assertHasAuthorization(ManagerAuthorization.desktopProcessesLog);
        loadDesktopProcesses();
    }, [searchState, hasReasonForAccess]);

    return (
        <PageTemplate>
            <PageHeader title={translateKey('desktopProcess.desktopProcesses')} />
            <Form
                onSubmit={async (e: React.FormEvent) => {
                    e.preventDefault();
                    await loadDesktopProcesses();
                }}
                submitOnEnter={true}
            >
                <Box
                    alignItems="start"
                    display="grid"
                    gap={3}
                    gridTemplateColumns={['1fr', null, '380px max-content max-content']}
                    mb={4}
                >
                    <OrganizationSelection
                        onChange={async (value) => {
                            setHasReasonForAccess(hasReasonForAccess && searchState.organizationId === value);
                            setSearchState({ ...searchState, organizationId: value, page: 1 });
                        }}
                        placeholder={translateKey('organization.name')}
                        showCity
                        value={searchState.organizationId}
                    />

                    <Button type="submit">{translateKey('common.search')}</Button>
                    <Button
                        isDisabled={!searchState.organizationId || total === 0 || hasReasonForAccess}
                        onClick={async () => setShowReasonModal(true)}
                        variant="outline"
                    >
                        {translateKey('desktopProcess.showContent')}
                    </Button>
                </Box>
            </Form>

            {searchState.organizationId && showReasonModal && !hasReasonForAccess && (
                <DesktopProcessAccessReasonsModal
                    onClose={async () => setShowReasonModal(false)}
                    onReasonGiven={async () => {
                        setHasReasonForAccess(true);
                        setShowReasonModal(false);
                    }}
                    organizationId={searchState.organizationId}
                />
            )}

            {desktopProcesses.map((desktopProcess, i) => (
                <TableContainer key={i}>
                    <Table spacing="medium">
                        <TableBody>
                            <TableRow key={desktopProcess.id}>
                                <TableCell>{desktopProcess.request ? desktopProcess.request.processId : ''}</TableCell>
                                <TableCell>
                                    {desktopProcess.request ? <FullName {...desktopProcess.request.patient} /> : ''}
                                </TableCell>
                                <TableCell>{desktopProcess.action}</TableCell>
                                <TableCell>{desktopProcess.status}</TableCell>
                                <TableCell>{<FullName {...desktopProcess.employment.practitioner} />}</TableCell>
                                <TableCell>
                                    <NullableFormattedDatetime value={desktopProcess.created_at} />
                                </TableCell>
                                <TableCell>
                                    <NullableFormattedDatetime value={desktopProcess.updated_at} />
                                </TableCell>
                            </TableRow>
                            {desktopProcess.params && (
                                <TableRow key={desktopProcess.id + 'params'}>
                                    <TableCell>{'params'}</TableCell>
                                    <TableCell colSpan={4}>
                                        <pre>{JSON.stringify(desktopProcess.params, undefined, 4)}</pre>
                                    </TableCell>
                                    <TableCell></TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            )}
                            {desktopProcess.messages &&
                                desktopProcess.messages.length > 0 &&
                                desktopProcess.messages.map((message) => (
                                    <TableRow key={message.id}>
                                        <TableCell>{message.type}</TableCell>
                                        <TableCell colSpan={4}>
                                            <pre>
                                                {message.content ? JSON.stringify(message.content, undefined, 4) : ''}
                                            </pre>
                                        </TableCell>
                                        <TableCell>
                                            <NullableFormattedDatetime value={message.created_at} />
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            ))}

            {isLoading && <Loader centered />}
            {!isLoading && desktopProcesses.length === 0 && (
                <HeaderGroup
                    alignment="center"
                    header={translateKey('common.noResult')}
                />
            )}
            <ListPager
                isDisabled={isLoading}
                itemsPerPage={searchState.itemsPerPage}
                onChangeItemsPerPage={(newItemsPerPage) =>
                    setSearchState({ ...searchState, itemsPerPage: newItemsPerPage, page: 1 })
                }
                onChangePage={async (newPage) => setSearchState({ ...searchState, page: newPage })}
                page={searchState.page}
                total={total}
            />
        </PageTemplate>
    );

    async function loadDesktopProcesses() {
        if (searchState.organizationId) {
            setIsLoading(true);
            const url = new UrlBuilder('/api/desktopProcesses')
                .add('organizationId', searchState.organizationId)
                .add('includeListAuthorizations', 'canSeeDesktopProcessDetails')
                .add('page', searchState.page)
                .add('limit', searchState.itemsPerPage)
                .build();

            const result = await fetchJson(url, 'GET');
            setTotal(result.json.total);
            setDesktopProcesses(result.json.items);
            setHasReasonForAccess(result.json.authorization.canSeeDesktopProcessDetails);
            setIsLoading(false);
        } else {
            setTotal(0);
            setDesktopProcesses([]);
            setHasReasonForAccess(false);
        }
    }
}
